import { HttpError } from '@littlebird/auth-client';
import {
  buildIntegrationList,
  projectPendingPermissions,
  type IntegrationData,
  type IntegrationIssue,
  type IntegrationProvider,
  type IntegrationSettings,
  type IntegrationSources,
  type PendingPermissionOperation,
  type ProviderKey,
  type ProviderTool,
  type ProviderTools,
  type ToolBehavior,
  type ToolPermission,
} from '@littlebird/integrations';
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { backendApis } from '@/backendApis';
import {
  availableIntegrationsQueryOptions,
  availableForThisDevice,
  integrationKeys,
  useConnectedAccountsQuery,
} from '@/features/integrations/integrations.query';
import {
  mcpKeys,
  useMCPCatalogQuery,
  useMCPServersQuery,
} from '@/features/mcp/mcp.query';
import { msgChannelKeys } from '@/features/msg-channels/common/msg-channels.query';
import {
  AXON_MCP_SERVERS_QUERY_KEY,
  useMcpServersQuery as useLocalMcpServersQuery,
} from '@/features/settings/sub-settings/settings-local-mcp.query';
import {
  isConnectableDeviceToolkit,
  useDevicePermissionGrants,
} from '@/integrations/device-integrations';
import { inBrowser } from '@/utils/capabilities';

import {
  toComposioAccounts,
  toComposioCatalog,
  toLocalIntegrations,
  toMcpAccounts,
  toMcpCatalog,
  type NativeDeviceToolkitInput,
} from './integration-management.adapters';

export const integrationManagementKeys = {
  all: ['integration-management'] as const,
  providers: () => ['integration-management', 'providers'] as const,
  tools: (key: ProviderKey) =>
    ['integration-management', 'tools', key] as const,
  // Distinct from `all` so a disabled tools query doesn't share a cache
  // entry with the broad invalidation prefix.
  toolsDisabled: ['integration-management', 'tools', 'disabled'] as const,
};

export interface IntegrationProviderState {
  providers: readonly IntegrationProvider[];
  isInitialLoading: boolean;
  issues: Array<{
    source: IntegrationIssue['source'];
    message: string;
    retry(): void;
  }>;
}

type SourceQuery<T> = Pick<UseQueryResult<T>, 'data' | 'isPending' | 'isError'>;

/** Maps one backend query's live state into the package's `IntegrationData`
 * state machine. `refreshing` (no `lastKnown`) only for a query that has
 * never resolved; a background refetch over already-successful data stays
 * `current` so it doesn't flash stale/loading UI. Takes the query's raw
 * data/isPending/isError rather than the query object itself, so callers can
 * memoize on just those — the query object's own identity isn't stable
 * across renders. */
function toIntegrationData<TRaw, TMapped>(
  data: TRaw | undefined,
  isPending: boolean,
  isError: boolean,
  map: (raw: TRaw) => TMapped,
  issue: IntegrationIssue
): IntegrationData<TMapped> {
  if (isPending) return { state: 'refreshing' };
  if (isError) {
    return {
      state: 'failed',
      lastKnown: data === undefined ? undefined : map(data),
      issue,
    };
  }
  return { state: 'current', value: map(data as TRaw) };
}

function toStateIssue<T>(
  data: IntegrationData<T>,
  retry: () => void
): IntegrationProviderState['issues'][number] | null {
  return data.state === 'failed'
    ? { source: data.issue.source, message: data.issue.message, retry }
    : null;
}

// Hoisted so `useMemo` below can key on the underlying query's own
// data/isPending/isError instead of these — a literal recreated every render
// would otherwise defeat that memoization.
const composioAccountsIssue: IntegrationIssue = {
  source: 'composio',
  code: 'composio_accounts_refresh_failed',
  message: "Couldn't refresh your connected accounts.",
  retryable: true,
};
const composioCatalogIssue: IntegrationIssue = {
  source: 'composio_catalog',
  code: 'composio_catalog_refresh_failed',
  message: "Couldn't refresh available integrations.",
  retryable: true,
};
const mcpAccountsIssue: IntegrationIssue = {
  source: 'mcp',
  code: 'mcp_accounts_refresh_failed',
  message: "Couldn't refresh your MCP servers.",
  retryable: true,
};
const mcpCatalogIssue: IntegrationIssue = {
  source: 'mcp_catalog',
  code: 'mcp_catalog_refresh_failed',
  message: "Couldn't refresh the MCP catalog.",
  retryable: true,
};
const localIntegrationsIssue: IntegrationIssue = {
  source: 'local',
  code: 'local_integrations_refresh_failed',
  message: "Couldn't refresh your on-device integrations.",
  retryable: true,
};

function mapComposioAccounts({
  available,
  accounts,
}: {
  available: Parameters<typeof toComposioAccounts>[0];
  accounts: Parameters<typeof toComposioAccounts>[1];
}): ReturnType<typeof toComposioAccounts> {
  return toComposioAccounts(available, accounts);
}

export function useIntegrationProviders(): IntegrationProviderState {
  const queryClient = useQueryClient();
  const browserMode = inBrowser();

  const availableIntegrationsQuery = useQuery({
    ...availableIntegrationsQueryOptions(),
    select: availableForThisDevice,
  });
  const connectedAccountsQuery = useConnectedAccountsQuery();
  const mcpServersQuery = useMCPServersQuery();
  const mcpCatalogQuery = useMCPCatalogQuery();
  const localMcpQuery = useLocalMcpServersQuery();
  const deviceGranted = useDevicePermissionGrants();

  const composioSourceQuery = useMemo<
    SourceQuery<{
      available: NonNullable<typeof availableIntegrationsQuery.data>;
      accounts: NonNullable<typeof connectedAccountsQuery.data>;
    }>
  >(
    () => ({
      data:
        availableIntegrationsQuery.data !== undefined &&
        connectedAccountsQuery.data !== undefined
          ? {
              available: availableIntegrationsQuery.data,
              accounts: connectedAccountsQuery.data,
            }
          : undefined,
      isPending:
        availableIntegrationsQuery.isPending ||
        connectedAccountsQuery.isPending,
      isError:
        availableIntegrationsQuery.isError || connectedAccountsQuery.isError,
    }),
    [
      availableIntegrationsQuery.data,
      availableIntegrationsQuery.isPending,
      availableIntegrationsQuery.isError,
      connectedAccountsQuery.data,
      connectedAccountsQuery.isPending,
      connectedAccountsQuery.isError,
    ]
  );

  // Memoized on each underlying query's own data/isPending/isError so
  // `sources` and `list` below only recompute — and only rewrite the query
  // cache — when a source's actual state changes, not on every render.
  const composioAccounts = useMemo(
    () =>
      toIntegrationData(
        composioSourceQuery.data,
        composioSourceQuery.isPending,
        composioSourceQuery.isError,
        mapComposioAccounts,
        composioAccountsIssue
      ),
    [
      composioSourceQuery.data,
      composioSourceQuery.isPending,
      composioSourceQuery.isError,
    ]
  );
  const composioCatalog = useMemo(
    () =>
      toIntegrationData(
        availableIntegrationsQuery.data,
        availableIntegrationsQuery.isPending,
        availableIntegrationsQuery.isError,
        toComposioCatalog,
        composioCatalogIssue
      ),
    [
      availableIntegrationsQuery.data,
      availableIntegrationsQuery.isPending,
      availableIntegrationsQuery.isError,
    ]
  );
  const mcpAccounts = useMemo(
    () =>
      toIntegrationData(
        mcpServersQuery.data,
        mcpServersQuery.isPending,
        mcpServersQuery.isError,
        toMcpAccounts,
        mcpAccountsIssue
      ),
    [mcpServersQuery.data, mcpServersQuery.isPending, mcpServersQuery.isError]
  );
  const mcpCatalog = useMemo(
    () =>
      toIntegrationData(
        mcpCatalogQuery.data,
        mcpCatalogQuery.isPending,
        mcpCatalogQuery.isError,
        toMcpCatalog,
        mcpCatalogIssue
      ),
    [mcpCatalogQuery.data, mcpCatalogQuery.isPending, mcpCatalogQuery.isError]
  );
  const nativeIntegrations = useMemo<NativeDeviceToolkitInput[]>(
    () =>
      (availableIntegrationsQuery.data ?? [])
        .filter(
          (info) =>
            info.on_device && isConnectableDeviceToolkit(info.toolkit_name)
        )
        .map((info) => ({
          info,
          granted: deviceGranted[info.toolkit_name] ?? false,
        })),
    [availableIntegrationsQuery.data, deviceGranted]
  );

  // Browser (and mobile) never supply localIntegrations — buildIntegrationList
  // then builds no on-device provider at all, matching the design constraint.
  const localIntegrations = useMemo(
    () =>
      browserMode
        ? undefined
        : toIntegrationData(
            localMcpQuery.data,
            localMcpQuery.isPending,
            localMcpQuery.isError,
            (servers) =>
              toLocalIntegrations({
                localServers: servers,
                nativeIntegrations,
              }),
            localIntegrationsIssue
          ),
    [
      browserMode,
      localMcpQuery.data,
      localMcpQuery.isPending,
      localMcpQuery.isError,
      nativeIntegrations,
    ]
  );

  const sources: IntegrationSources = useMemo(
    () => ({
      composioAccounts,
      composioCatalog,
      mcpAccounts,
      mcpCatalog,
      channels: [],
      localIntegrations,
    }),
    [
      composioAccounts,
      composioCatalog,
      mcpAccounts,
      mcpCatalog,
      localIntegrations,
    ]
  );

  const list = useMemo(() => buildIntegrationList(sources), [sources]);

  useEffect(() => {
    queryClient.setQueryData(integrationManagementKeys.providers(), list);
  }, [queryClient, list]);

  const issues = [
    toStateIssue(composioAccounts, () => {
      void availableIntegrationsQuery.refetch();
      void connectedAccountsQuery.refetch();
    }),
    // composioAccounts already covers an availableIntegrationsQuery failure
    // (its combined query ORs in that same isError) and its retry above
    // refetches it too — only surface the catalog's own banner in the
    // transient window before composioAccounts has caught up to failed,
    // otherwise this doubles up on the banner above for one failed query.
    composioAccounts.state === 'failed'
      ? null
      : toStateIssue(composioCatalog, () => {
          void availableIntegrationsQuery.refetch();
        }),
    toStateIssue(mcpAccounts, () => {
      void mcpServersQuery.refetch();
    }),
    toStateIssue(mcpCatalog, () => {
      void mcpCatalogQuery.refetch();
    }),
    localIntegrations
      ? toStateIssue(localIntegrations, () => {
          void localMcpQuery.refetch();
        })
      : null,
  ].flatMap((issue) => (issue ? [issue] : []));

  const isInitialLoading =
    availableIntegrationsQuery.isPending ||
    connectedAccountsQuery.isPending ||
    mcpServersQuery.isPending ||
    mcpCatalogQuery.isPending ||
    (!browserMode && localMcpQuery.isPending);

  return {
    providers: list.integrations,
    isInitialLoading,
    issues,
  };
}

export function useProviderToolsQuery(
  key: ProviderKey | undefined,
  enabled: boolean
): UseQueryResult<ProviderTools> {
  return useQuery({
    queryKey: key
      ? integrationManagementKeys.tools(key)
      : integrationManagementKeys.toolsDisabled,
    queryFn:
      key && enabled
        ? ({ signal }) => backendApis.providerTools.getTools(key, { signal })
        : skipToken,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

function toolPermissionMutationScope(providerKey: ProviderKey) {
  return { id: `integration-tools:${providerKey}` };
}

/** Both permission mutations share one scope per provider, so TanStack Query
 * runs same-provider writes one at a time (a later one waits for the one in
 * front of it) while different providers' writes still run in parallel. */
export function useSetToolPermissionMutation(
  providerKey: ProviderKey
): UseMutationResult<
  void,
  Error,
  { toolSlug: string; permission: ToolPermission }
> {
  const queryClient = useQueryClient();
  return useMutation({
    scope: toolPermissionMutationScope(providerKey),
    mutationFn: ({
      toolSlug,
      permission,
    }: {
      toolSlug: string;
      permission: ToolPermission;
    }) =>
      backendApis.providerTools
        .setToolPermission(providerKey, toolSlug, permission)
        .then(() => undefined),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: integrationManagementKeys.tools(providerKey),
      }),
  });
}

export function useSetBehaviorPermissionMutation(
  providerKey: ProviderKey
): UseMutationResult<
  void,
  Error,
  { behavior: ToolBehavior; permission: ToolPermission }
> {
  const queryClient = useQueryClient();
  return useMutation({
    scope: toolPermissionMutationScope(providerKey),
    mutationFn: ({
      behavior,
      permission,
    }: {
      behavior: ToolBehavior;
      permission: ToolPermission;
    }) =>
      backendApis.providerTools
        .setGroupPermission(providerKey, behavior, permission)
        .then(() => undefined),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: integrationManagementKeys.tools(providerKey),
      }),
  });
}

function isIncompleteInventoryError(error: unknown): boolean {
  if (!(error instanceof HttpError) || error.status !== 409) return false;
  return error.body?.detail?.code === 'provider_inventory_incomplete';
}

function operationFailureMessage(
  operation: PendingPermissionOperation,
  error: unknown
): string {
  if (operation.kind === 'behavior' && isIncompleteInventoryError(error)) {
    return "Some of this group's tools haven't finished loading yet — try again in a moment.";
  }
  return operation.kind === 'tool'
    ? "Couldn't update this tool's permission."
    : "Couldn't update this group's permission.";
}

export interface ProviderToolsPanel {
  tools: readonly ProviderTool[];
  discovery: ProviderTools['discovery'] | undefined;
  isInitialLoading: boolean;
  loadErrorMessage: string | null;
  isRefetchError: boolean;
  retry(): void;
  setToolPermission(toolSlug: string, permission: ToolPermission): void;
  setBehaviorPermission(
    behavior: ToolBehavior,
    permission: ToolPermission
  ): void;
  failure: { operation: PendingPermissionOperation; message: string } | null;
}

/** Wires the tools query and the two permission mutations for one provider's
 * Tools tab: an in-flight write shows its optimistic value immediately
 * (`projectPendingPermissions`), and a failed write drops its own overlay so
 * the tab falls back to whatever the query still has confirmed — a failed
 * group write reverts every tool in that group at once, since they all
 * project off the same one pending operation. */
export function useProviderToolsPanel(
  provider: IntegrationProvider,
  enabled = true
): ProviderToolsPanel {
  const toolsQuery = useProviderToolsQuery(
    provider.key,
    provider.hasTools && enabled
  );
  const setToolPermissionMutation = useSetToolPermissionMutation(provider.key);
  const setBehaviorPermissionMutation = useSetBehaviorPermissionMutation(
    provider.key
  );
  const [pendingOperations, setPendingOperations] = useState<
    readonly PendingPermissionOperation[]
  >([]);
  const [failure, setFailure] = useState<ProviderToolsPanel['failure']>(null);

  function run(
    operation: PendingPermissionOperation,
    mutateAsync: () => Promise<void>
  ): void {
    setFailure(null);
    setPendingOperations((current) => [...current, operation]);
    mutateAsync()
      .then(() => {
        // A later write can succeed after an earlier one already failed and
        // left its message up — clear it so success doesn't leave a stale
        // error on screen for an operation that's no longer pending.
        setFailure(null);
      })
      .catch((error: unknown) => {
        setFailure({
          operation,
          message: operationFailureMessage(operation, error),
        });
      })
      .finally(() => {
        setPendingOperations((current) =>
          current.filter((pending) => pending !== operation)
        );
      });
  }

  const confirmedTools = toolsQuery.data?.tools ?? [];

  return {
    tools: projectPendingPermissions(confirmedTools, pendingOperations),
    discovery: toolsQuery.data?.discovery,
    isInitialLoading: toolsQuery.isPending,
    loadErrorMessage:
      toolsQuery.isError && toolsQuery.data === undefined
        ? "Couldn't load tools."
        : null,
    isRefetchError: toolsQuery.isError && toolsQuery.data !== undefined,
    retry: () => void toolsQuery.refetch(),
    setToolPermission: (toolSlug, permission) =>
      run({ kind: 'tool', toolSlug, permission }, () =>
        setToolPermissionMutation.mutateAsync({ toolSlug, permission })
      ),
    setBehaviorPermission: (behavior, permission) =>
      run({ kind: 'behavior', behavior, permission }, () =>
        setBehaviorPermissionMutation.mutateAsync({ behavior, permission })
      ),
    failure,
  };
}

export function isChannelSetting(
  setting: IntegrationSettings
): setting is Extract<IntegrationSettings, { kind: 'channel' }> {
  return setting.kind === 'channel';
}

/** Invalidates the one backend cache that owns the account/installation data
 * for `provider`'s kind, so the next render of `useIntegrationProviders`
 * picks up the account/channel/installation change that just happened. */
export async function invalidateProviderAfterAccountChange(
  queryClient: QueryClient,
  provider: IntegrationProvider
): Promise<void> {
  // Removing, pausing or reconnecting an account can add or drop its try entry on Home and Integrations
  void queryClient.invalidateQueries({
    queryKey: integrationKeys.suggestions(),
  });
  switch (provider.kind) {
    case 'composio':
      await queryClient.invalidateQueries({
        queryKey: integrationKeys.lists(),
      });
      return;
    case 'catalog_mcp':
    case 'custom_mcp':
      await queryClient.invalidateQueries({ queryKey: mcpKeys.servers() });
      return;
    case 'device':
      await queryClient.invalidateQueries({
        queryKey: AXON_MCP_SERVERS_QUERY_KEY,
      });
      return;
    case 'channel': {
      const channelSetting = provider.settings.find(isChannelSetting);
      if (channelSetting) {
        await queryClient.invalidateQueries({
          queryKey: msgChannelKeys.connection(channelSetting.channel),
        });
      }
    }
  }
}
